import {
  contributionLimitForAccountType,
  type ContributionLimit,
  type LifePlanBucket,
  type LifePlanMember,
} from '@zeni/shared'
import { calculateAge } from './age'

/**
 * ライフプラン表の展開。年単位（列 = 西暦）と月単位（列 = 1〜12 月）の
 * どちらも同じ LifePlanTable の形で返す。
 *
 * 今期までは売買明細から再現した実績をそのまま並べる（拠出額・元本・評価額・
 * その期間の利回り）。その先は枠ごとの期待利回りと年間拠出額で積み上げる。
 *
 *   元本   = 前年の元本 + その年の拠出額
 *   評価額 = 前年の評価額 × (1 + 期待利回り) + その年の拠出額
 *
 * 月単位は同じ見通しを月割りにする（拠出は 1/12 ずつ、利回りは (1+r)^(1/12)）。
 *
 * 将来の拠出は制度の枠に収める。年間投資枠を超える分は切り、非課税保有限度額
 * （NISA は 1,800 万円。同じ人のつみたて投資枠と成長投資枠、こども NISA で分け合う）に
 * 達したらそれ以降は積まない。こども NISA のように年齢で終わる制度は、持ち主が対象年齢を
 * 過ぎた年から拠出を止める。枠の判定は預り区分の名前から行う。
 */

export interface LifePlanSeries {
  bucket: LifePlanBucket
  /** 表の見出しに出す枠の名前（例: 「NISAつみたて投資枠：悟」） */
  label: string
  /** 預り区分から見分けた制度と、その拠出枠 */
  limit: ContributionLimit
  /** 来年以降に当てる期待利回り（％）。未設定なら null（0% で積み上げる） */
  expectedRate: number | null
  /** その期間に入れた（入れる）額 */
  contributions: number[]
  /** 期末時点の元本累計 */
  principals: number[]
  /** 期末時点の評価額。基準価額が分からない過去の期間は null */
  values: (number | null)[]
  /** 期末時点の評価損益（評価額 − 元本）。評価額が分からない期間は null */
  gains: (number | null)[]
  /** その期間の利回り（％）。今期までは実績、その先は期待利回り */
  returnRates: (number | null)[]
  /**
   * 今年時点で使っている非課税保有限度額（簿価）。NISA は同じ人の枠を合算した額。
   * 生涯枠のない制度は null。
   */
  lifetimeUsed: number | null
  /** 今年時点でこの枠だけで使っている額（成長投資枠の内枠 1,200 万円の判定に使う） */
  schemeLifetimeUsed: number | null
  /** 非課税保有限度額に達して拠出を打ち切った最初の年。届かないなら null */
  lifetimeFilledYear: number | null
}

export interface LifePlanAgeRow {
  member: LifePlanMember
  /** 各列の期末時点（年単位なら 12/31、月単位なら月末）の満年齢。未登録なら null */
  ages: (number | null)[]
}

export interface LifePlanTable {
  /** 列の単位。列見出しの読み方が変わる */
  unit: 'year' | 'month'
  /** 列の値。年単位なら西暦、月単位なら 1〜12 */
  columns: number[]
  ageRows: LifePlanAgeRow[]
  series: LifePlanSeries[]
  /** 各列の拠出額合計 */
  totalContributions: number[]
  /** 各列の元本合計 */
  totalPrincipals: number[]
  /** 各列の評価額合計 = 金融資産残高 */
  totalValues: number[]
  /** 各列の評価損益合計（金融資産残高 − 元本合計）。期末時点の累計 */
  totalGains: number[]
  /** その期間に増減した評価損益（残高の増加のうち拠出でない分）。期首の残高が分からない列は null */
  totalPeriodGains: (number | null)[]
  /** 全体の利回り（％）。各列の残高と拠出から求めた期間リターン */
  totalReturnRates: (number | null)[]
  /**
   * 左端の列に入る前の残高（年単位なら startYear の前年末、月単位なら前年 12 月末）。
   * 期間まるごとの利回りを出すのに使う。前がどうだったか分からなければ null。
   */
  openingValue: number | null
}

export interface LifePlanLabels {
  /** 預り区分が空の枠の呼び名 */
  noAccountType: string
  /** 持ち主が未設定の枠の呼び名 */
  noMember: string
}

/** 枠の表示名。「預り区分：持ち主」の形にして、元にした表と並びを揃える */
export function bucketLabel(bucket: LifePlanBucket, labels: LifePlanLabels): string {
  const accountType = bucket.accountType || labels.noAccountType
  return `${accountType}：${bucket.memberName ?? labels.noMember}`
}

/**
 * 来年以降のその年に入れる額（制度の枠を当てる前）。拠出をやめる日が年の途中なら、
 * その年は月割りにする（6 月末までなら年間拠出額の 6/12）。
 */
function plannedContribution(bucket: LifePlanBucket, year: number): number {
  const end = bucket.contributionEndDate
  if (end === null) return bucket.annualAmount
  const endYear = Number(end.slice(0, 4))
  const endMonth = Number(end.slice(5, 7))
  if (!Number.isInteger(endYear) || !Number.isInteger(endMonth)) return bucket.annualAmount
  if (year < endYear) return bucket.annualAmount
  if (year > endYear) return 0
  return (bucket.annualAmount * endMonth) / 12
}

/**
 * その年に年齢の条件を満たしているか。こども NISA のように「その年の 1 月 1 日時点で
 * 18 歳未満」が条件の制度で使う。生年月日が未登録なら判定できないので止めない。
 */
function eligibleByAge(limit: ContributionLimit, birthDate: string | null | undefined, year: number): boolean {
  if (limit.eligibleAgeBelow === null) return true
  const age = calculateAge(birthDate, new Date(year, 0, 1))
  if (age === null) return true
  return age < limit.eligibleAgeBelow
}

/**
 * 制度の年間投資枠に収めた額。iDeCo のように加入区分で枠が変わる制度は
 * 目安しか分からないので抑えない。
 */
export function withinAnnualLimit(amount: number, limit: ContributionLimit): number {
  if (limit.closed) return 0
  if (limit.annualLimit === null || limit.annualLimitVaries) return amount
  return Math.min(amount, limit.annualLimit)
}

/**
 * 期間リターン（％）。Modified Dietz 法。期間内の拠出は期央にあったとみなす。
 * バックエンドが実績に使っているのと同じ式で、全体の行もここで揃える。
 */
function periodReturnPercent(
  openingValue: number | null,
  closingValue: number | null,
  contribution: number,
): number | null {
  if (openingValue === null || closingValue === null) return null
  const base = openingValue + contribution / 2
  if (base <= 0) return null
  return Math.round(((closingValue - openingValue - contribution) / base) * 10000) / 100
}

interface Working {
  series: LifePlanSeries
  rate: number
  actualOf: Map<number, LifePlanBucket['actuals'][number]>
}

/** 生涯枠の使用状況を数えるキー。NISA の枠は人ごとにまとめて数える */
function groupUsageKey(memberKey: string, group: string) {
  return `${memberKey}|group:${group}`
}
function schemeUsageKey(memberKey: string, scheme: string) {
  return `${memberKey}|scheme:${scheme}`
}

export function buildLifePlan({
  buckets,
  members,
  startYear,
  endYear,
  pivotYear,
  dataStartYear,
  labels,
}: {
  buckets: LifePlanBucket[]
  members: LifePlanMember[]
  startYear: number
  endYear: number
  /** 実績と見通しの境目になる年（= 今年） */
  pivotYear: number
  /** 売買明細がある最初の年。左端がこの年なら、その前年は残高ゼロと分かる */
  dataStartYear: number | null
  labels: LifePlanLabels
}): LifePlanTable {
  const span = Math.max(0, endYear - startYear + 1)
  const years = Array.from({ length: span }, (_, i) => startYear + i)
  // 実績と見通しの境目。表が今年を含まないときは近い端に寄せる
  const pivot = Math.min(Math.max(pivotYear, startYear), endYear)
  const pivotIndex = years.length === 0 ? 0 : pivot - startYear

  const works: Working[] = buckets.map((bucket) => {
    const limit = contributionLimitForAccountType(bucket.accountType)
    const expectedRate = bucket.annualReturnRate
    const series: LifePlanSeries = {
      bucket,
      label: bucketLabel(bucket, labels),
      limit,
      expectedRate,
      contributions: new Array<number>(years.length).fill(0),
      principals: new Array<number>(years.length).fill(0),
      values: new Array<number | null>(years.length).fill(null),
      gains: new Array<number | null>(years.length).fill(null),
      returnRates: new Array<number | null>(years.length).fill(null),
      lifetimeUsed: null,
      schemeLifetimeUsed: null,
      lifetimeFilledYear: null,
    }
    return {
      series,
      rate: (expectedRate ?? 0) / 100,
      actualOf: new Map(bucket.actuals.map((a) => [a.year, a])),
    }
  })

  // 今年までの実績を埋める
  for (const { series, actualOf } of works) {
    for (let i = 0; i <= pivotIndex && i < years.length; i += 1) {
      const actual = actualOf.get(years[i])
      if (actual) {
        series.contributions[i] = actual.contribution
        series.principals[i] = actual.principal
        series.values[i] = actual.value
        series.returnRates[i] = actual.returnRate
      } else {
        // 明細のない年。保有を始める前なので 0
        series.values[i] = 0
      }
    }
    // 明細をまったく持たない枠は、今年の列に現在の保有データを置く
    if (years.length > 0 && !actualOf.has(pivot)) {
      series.principals[pivotIndex] = series.bucket.currentPrincipal
      series.values[pivotIndex] = series.bucket.currentValue
    }
  }

  // 非課税保有限度額の使用状況。今年時点の元本（簿価）を出発点にする
  const used = new Map<string, number>()
  const addUsage = (key: string, amount: number) => used.set(key, (used.get(key) ?? 0) + amount)
  for (const { series } of works) {
    const { limit, bucket } = series
    const principal = series.principals[pivotIndex] ?? 0
    if (limit.lifetimeGroup !== null) addUsage(groupUsageKey(bucket.memberKey, limit.lifetimeGroup), principal)
    if (limit.lifetimeLimit !== null) addUsage(schemeUsageKey(bucket.memberKey, limit.scheme), principal)
  }
  for (const { series } of works) {
    const { limit, bucket } = series
    series.lifetimeUsed = limit.lifetimeGroup !== null
      ? used.get(groupUsageKey(bucket.memberKey, limit.lifetimeGroup)) ?? 0
      : limit.lifetimeLimit !== null
      ? used.get(schemeUsageKey(bucket.memberKey, limit.scheme)) ?? 0
      : null
    series.schemeLifetimeUsed = limit.lifetimeLimit === null
      ? null
      : used.get(schemeUsageKey(bucket.memberKey, limit.scheme)) ?? 0
  }

  // 年齢で拠出できる期間が決まる制度（こども NISA）の判定に使う
  const birthDateOf = new Map(members.map((member) => [member.id, member.birthDate]))

  // 将来は年を外側にして回す。NISA の生涯枠は同じ人の枠で分け合うので、
  // 枠ごとに独立して積み上げると上限を超えてしまう
  for (let i = pivotIndex + 1; i < years.length; i += 1) {
    const year = years[i]
    for (const { series, rate } of works) {
      const { limit, bucket } = series
      const eligible = eligibleByAge(limit, bucket.memberId === null ? null : birthDateOf.get(bucket.memberId), year)
      let contribution = eligible ? withinAnnualLimit(plannedContribution(bucket, year), limit) : 0

      // 非課税保有限度額の残り。成長投資枠のように内枠がある制度は両方で抑える
      const caps: number[] = []
      const gKey = limit.lifetimeGroup === null ? null : groupUsageKey(bucket.memberKey, limit.lifetimeGroup)
      const sKey = limit.lifetimeLimit === null ? null : schemeUsageKey(bucket.memberKey, limit.scheme)
      if (gKey !== null && limit.groupLifetimeLimit !== null) {
        caps.push(limit.groupLifetimeLimit - (used.get(gKey) ?? 0))
      }
      if (sKey !== null && limit.lifetimeLimit !== null) {
        caps.push(limit.lifetimeLimit - (used.get(sKey) ?? 0))
      }
      if (caps.length > 0) {
        const room = Math.max(0, Math.min(...caps))
        if (contribution > room) {
          contribution = room
          if (series.lifetimeFilledYear === null) series.lifetimeFilledYear = year
        }
      }

      if (gKey !== null) addUsage(gKey, contribution)
      if (sKey !== null) addUsage(sKey, contribution)

      const previous = series.values[i - 1] ?? 0
      series.contributions[i] = contribution
      series.principals[i] = series.principals[i - 1] + contribution
      series.values[i] = previous * (1 + rate) + contribution
      // 見通しの区間は、当てた期待利回りをそのまま利回り行に出す
      series.returnRates[i] = series.expectedRate
    }
  }

  const series = works.map((w) => w.series)

  // 評価損益は評価額と元本の差。評価額が分からない年は出さない
  for (const s of series) {
    s.gains = s.values.map((value, i) => (value === null ? null : value - s.principals[i]))
  }

  const sumOf = (pick: (s: LifePlanSeries) => number[]) =>
    years.map((_, i) => series.reduce((sum, s) => sum + pick(s)[i], 0))

  const totalContributions = sumOf((s) => s.contributions)
  const totalPrincipals = sumOf((s) => s.principals)
  // 評価額が分からない年は 0 として足す（枠ごとの行では「—」のまま出す）
  const totalValues = years.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0))
  // 左端がデータの最初の年なら、その前年は「まだ何も持っていない」= 0 から計算できる。
  // 途中の年から表示している場合は前年の残高が分からないので出さない
  const openingAtStart = dataStartYear !== null && startYear <= dataStartYear ? 0 : null
  const totalGains = years.map((_, i) => totalValues[i] - totalPrincipals[i])
  // その年に増減した分。残高の増加から拠出を除いた額（= 累計の評価損益の前年差）
  const totalPeriodGains = years.map((_, i) => {
    const opening = i === 0 ? openingAtStart : totalValues[i - 1]
    if (opening === null) return null
    return totalValues[i] - opening - totalContributions[i]
  })
  const totalReturnRates = years.map((_, i) =>
    periodReturnPercent(i === 0 ? openingAtStart : totalValues[i - 1], totalValues[i], totalContributions[i]),
  )

  const ageRows: LifePlanAgeRow[] = members.map((member) => ({
    member,
    // 列は年単位なので、その年の年末（12/31）時点の年齢で揃える
    ages: years.map((year) => calculateAge(member.birthDate, new Date(year, 11, 31))),
  }))

  return {
    unit: 'year',
    columns: years,
    openingValue: openingAtStart,
    ageRows,
    series,
    totalContributions,
    totalPrincipals,
    totalValues,
    totalGains,
    totalPeriodGains,
    totalReturnRates,
  }
}

/** 月末は月ごとに変わるので、翌月 0 日＝当月末日として求める */
function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 0)
}

/**
 * 来年以降の 1 か月ぶんの拠出額（制度の枠を当てたあと）。年間拠出額の 1/12 で、
 * 拠出をやめる日を過ぎた月と、年齢の条件を外れた年は 0 にする。
 */
function plannedMonthlyContribution(
  series: LifePlanSeries,
  birthDate: string | null | undefined,
  year: number,
  month: number,
): number {
  const { bucket, limit } = series
  if (!eligibleByAge(limit, birthDate, year)) return 0
  const end = bucket.contributionEndDate
  if (end !== null && `${year}-${String(month).padStart(2, '0')}` > end.slice(0, 7)) return 0
  return withinAnnualLimit(bucket.annualAmount, limit) / 12
}

/**
 * ある年の月単位の表。
 *
 * 経過した月はバックエンドが売買明細から再現した実額（monthActuals）をそのまま並べ、
 * まだ来ていない月は年間拠出額の月割りと期待利回りの月複利で見通す。
 * 年をまたぐ積み上げは年単位の表（annual）から引き継ぐので、
 * 1 月の期首は「前年 12 月末の元本・評価額」になる。
 */
export function buildLifePlanMonths({
  annual,
  members,
  year,
  currentYear,
  dataStartYear,
}: {
  /** 同じ枠・同じ前提で作った年単位の表。左端は year - 1 以前を含んでいること */
  annual: LifePlanTable
  members: LifePlanMember[]
  /** 列に出す年 */
  year: number
  /**
   * 今年。実績と見通しの境目は、枠ごとの monthActuals がどこまで入っているかで決まる
   * （バックエンドが今月までしか返さない）ので、ここでは年の前後だけを見る
   */
  currentYear: number
  /** 売買明細がある最初の年。year がこの年なら、その前年末は残高ゼロと分かる */
  dataStartYear: number | null
}): LifePlanTable {
  const months = Array.from({ length: 12 }, (_, i) => i + 1)
  const birthDateOf = new Map(members.map((member) => [member.id, member.birthDate]))

  // 年単位の表から、前年 12 月末（= 1 月の期首）とその年の拠出予定を引く
  const indexOfYear = (target: number) => annual.columns.indexOf(target)
  const prevIndex = indexOfYear(year - 1)
  const yearIndex = indexOfYear(year)

  const series: LifePlanSeries[] = annual.series.map((source) => {
    const openingPrincipal = prevIndex < 0 ? 0 : source.principals[prevIndex]
    const openingValue = prevIndex < 0 ? 0 : source.values[prevIndex] ?? 0
    // 見通しの月は年利を月複利に直す。12 か月ぶん掛けると年利に戻る形にしておく
    const rate = (source.expectedRate ?? 0) / 100
    const monthlyRate = source.expectedRate === null
      ? null
      : Math.round(((1 + rate) ** (1 / 12) - 1) * 10000) / 100
    const birthDate = source.bucket.memberId === null
      ? null
      : birthDateOf.get(source.bucket.memberId) ?? null

    const actualOf = new Map(source.bucket.monthActuals.map((a) => [a.month, a]))
    // 実績のある最後の月。今年なら今月、過去の年なら 12 月。実績が無ければ 0
    const lastActualMonth = actualOf.size === 0 ? 0 : Math.max(...actualOf.keys())

    /**
     * 見通しの月に入れる額。来年以降は年単位の表が制度の枠（年間投資枠・
     * 非課税保有限度額）を当てた拠出額を持っているので、それを 12 等分する。
     * 今年の残りの月は年単位の表が実績なので、枠の設定から月割りで引く。
     */
    const monthlyPlanFor = (month: number) =>
      year > currentYear && yearIndex >= 0
        ? source.contributions[yearIndex] / 12
        : plannedMonthlyContribution(source, birthDate, year, month)

    const contributions = new Array<number>(12).fill(0)
    const principals = new Array<number>(12).fill(0)
    const values = new Array<number | null>(12).fill(null)
    const returnRates = new Array<number | null>(12).fill(null)

    // 見通しの起点。実績のある月まではその月末、無ければ前年 12 月末から積む
    let basePrincipal = openingPrincipal
    let baseValue = openingValue
    // 起点からその月までに入れる予定の額の累計
    let planned = 0

    for (const month of months) {
      const i = month - 1
      const actual = actualOf.get(month)
      if (actual) {
        contributions[i] = actual.contribution
        principals[i] = actual.principal
        values[i] = actual.value
        returnRates[i] = actual.returnRate
        basePrincipal = actual.principal
        baseValue = actual.value ?? actual.principal
        continue
      }
      if (year < currentYear) {
        // 過去の年で明細が無い月は、まだ保有を始めていない（または売り切った）月。
        // 直前の月末の残高をそのまま横に伸ばす
        principals[i] = basePrincipal
        values[i] = baseValue
        continue
      }
      // 見通しの月。拠出は月割り、評価額は起点から月複利で伸ばす
      contributions[i] = monthlyPlanFor(month)
      planned += contributions[i]
      principals[i] = basePrincipal + planned
      values[i] = baseValue * (1 + rate) ** ((month - lastActualMonth) / 12) + planned
      returnRates[i] = monthlyRate
    }

    return {
      ...source,
      contributions,
      principals,
      values,
      gains: values.map((value, i) => (value === null ? null : value - principals[i])),
      returnRates,
    }
  })

  const sumOf = (pick: (s: LifePlanSeries) => number[]) =>
    months.map((_, i) => series.reduce((sum, s) => sum + pick(s)[i], 0))

  const totalContributions = sumOf((s) => s.contributions)
  const totalPrincipals = sumOf((s) => s.principals)
  const totalValues = months.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0))
  const totalGains = months.map((_, i) => totalValues[i] - totalPrincipals[i])

  // 1 月の期首は前年 12 月末の残高。表示する年が明細の最初の年なら 0 と分かる
  const openingValue = prevIndex >= 0
    ? annual.totalValues[prevIndex]
    : dataStartYear !== null && year <= dataStartYear
    ? 0
    : null
  const openingOf = (i: number) => (i === 0 ? openingValue : totalValues[i - 1])

  const totalPeriodGains = months.map((_, i) => {
    const opening = openingOf(i)
    if (opening === null) return null
    return totalValues[i] - opening - totalContributions[i]
  })
  const totalReturnRates = months.map((_, i) =>
    periodReturnPercent(openingOf(i), totalValues[i], totalContributions[i]),
  )

  const ageRows: LifePlanAgeRow[] = members.map((member) => ({
    member,
    // 列は月単位なので、その月末時点の年齢で揃える
    ages: months.map((month) => calculateAge(member.birthDate, lastDayOfMonth(year, month))),
  }))

  return {
    unit: 'month',
    columns: months,
    openingValue,
    ageRows,
    series,
    totalContributions,
    totalPrincipals,
    totalValues,
    totalGains,
    totalPeriodGains,
    totalReturnRates,
  }
}
